import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  Box,
  alpha,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Info,
  Search,
  HourglassEmpty,
  Timeline,
  Terminal,
  Edit,
  Store,
  Computer,
  Group,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";

function TableWhatsappCop({
  configuraciones = [],
  loading = false,
  error = null,
  onError = null,
  showNoData = false,
  totalConfiguraciones = 0,
  filtros = { grupo: "todos", activo: "todos", pulso: "todos", busqueda: "" },
  onEditClick,
}) {
  const [filteredConfiguraciones, setFilteredConfiguraciones] = useState([]);

  useEffect(() => {
    let filtered = [...configuraciones];

    if (filtros.busqueda) {
      const query = filtros.busqueda.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.nombreComercio?.toLowerCase().includes(query) ||
          item.idCommerce?.toString().includes(query) ||
          (item.idTerminal && item.idTerminal?.toString().includes(query)) ||
          item.grupoAlertaNOC?.toLowerCase().includes(query) ||
          item.groupName?.toLowerCase().includes(query) ||
          item.workflow?.toLowerCase().includes(query) ||
          item.terminalName?.toLowerCase().includes(query)
      );
    }

    if (filtros.activo === "activos") {
      filtered = filtered.filter((item) => item.activo === true);
    } else if (filtros.activo === "inactivos") {
      filtered = filtered.filter((item) => item.activo === false);
    }

    if (filtros.pulso === "conPulso") {
      filtered = filtered.filter((item) => item.pulso === true);
    } else if (filtros.pulso === "sinPulso") {
      filtered = filtered.filter((item) => item.pulso === false);
    }

    if (filtros.grupo !== "todos") {
      filtered = filtered.filter((item) => item.grupoAlertaNOC === filtros.grupo);
    }

    setFilteredConfiguraciones(filtered);
  }, [configuraciones, filtros]);

  // Columnas para la tabla - AÑADIDA COLUMNA "Grupo Nombre"
  const columns = [
    { Header: "ID Comercio", accessor: "idCommerce", width: "7%" },
    { Header: "Comercio", accessor: "nombreComercio", width: "15%" },
    { Header: "Estado", accessor: "estado", width: "6%" },
    { Header: "ID Terminal", accessor: "idTerminal", width: "7%" },
    { Header: "Terminal Name", accessor: "terminalName", width: "12%" },
    { Header: "Grupo NOC", accessor: "grupoAlertaNOC", width: "10%" },
    { Header: "Grupo WhatsApp", accessor: "groupName", width: "12%" }, // NUEVA COLUMNA
    { Header: "Workflow", accessor: "workflow", width: "10%" },
    { Header: "Pulso", accessor: "pulso", width: "6%" },
    { Header: "Acciones", accessor: "acciones", width: "5%" },
  ];

  // Filas para la tabla - AÑADIDO groupName
  const rows = filteredConfiguraciones.map((item) => ({
    idCommerce: (
      <MDTypography
        variant="body2"
        fontWeight="bold"
        sx={{ fontSize: "0.875rem", fontFamily: "'Roboto Mono', monospace" }}
      >
        {item.idCommerce}
      </MDTypography>
    ),
    idTerminal: (
      <MDTypography
        variant="body2"
        fontWeight="medium"
        color={item.idTerminal ? "text.primary" : "text.disabled"}
        sx={{ fontSize: "0.875rem", fontFamily: "'Roboto Mono', monospace" }}
      >
        {item.idTerminal || "—"}
      </MDTypography>
    ),
    nombreComercio: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Store fontSize="small" color={item.activo ? "success" : "disabled"} />
        <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: "0.875rem" }}>
          {item.nombreComercio}
        </MDTypography>
      </MDBox>
    ),
    terminalName: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Computer
          fontSize="small"
          color={item.terminalName && item.terminalName !== "Sin terminal" ? "primary" : "disabled"}
        />
        <MDTypography
          variant="body2"
          fontWeight="medium"
          color={
            item.terminalName && item.terminalName !== "Sin terminal"
              ? "text.primary"
              : "text.disabled"
          }
          sx={{ fontSize: "0.875rem" }}
        >
          {item.terminalName || "Sin terminal"}
        </MDTypography>
      </MDBox>
    ),
    grupoAlertaNOC: (
      <Chip
        label={item.grupoAlertaNOC || "Sin grupo"}
        size="small"
        sx={{
          fontSize: "0.7rem",
          height: "24px",
          backgroundColor: alpha("#075e54", 0.1),
          color: "#075e54",
          fontWeight: 500,
        }}
      />
    ),
    // NUEVA COLUMNA
    groupName: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Group
          fontSize="small"
          color={item.groupName && item.groupName !== "Sin grupo" ? "info" : "disabled"}
        />
        <MDTypography
          variant="body2"
          fontWeight="medium"
          color={
            item.groupName && item.groupName !== "Sin grupo" ? "text.primary" : "text.disabled"
          }
          sx={{ fontSize: "0.875rem" }}
        >
          {item.groupName || "Sin grupo"}
        </MDTypography>
      </MDBox>
    ),
    workflow: (
      <MDTypography
        variant="body2"
        color="text.secondary"
        fontWeight="medium"
        sx={{ fontSize: "0.8125rem" }}
      >
        {item.workflow || "—"}
      </MDTypography>
    ),
    estado: (
      <Chip
        label={item.activo ? "ACTIVO" : "INACTIVO"}
        size="small"
        color={item.activo ? "success" : "error"}
        icon={item.activo ? <CheckCircle /> : <Cancel />}
        sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "28px" }}
      />
    ),
    pulso: (
      <Chip
        label={item.pulso ? "CON" : "SIN"}
        size="small"
        color={item.pulso ? "info" : "default"}
        icon={item.pulso ? <Timeline /> : <Cancel />}
        sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "28px" }}
      />
    ),
    acciones: (
      <Box display="flex" gap={0.5}>
        <Tooltip title="Editar configuración" arrow>
          <IconButton size="small" color="info" onClick={() => onEditClick?.(item)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  const retryLoad = () => {
    if (onError && typeof onError === "function") {
      onError("reintentar");
    }
  };

  return (
    <MDBox>
      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <MDBox textAlign="center">
            <CircularProgress color="primary" size={60} />
            <MDTypography variant="h5" mt={2} fontWeight="bold">
              Cargando configuraciones...
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" mt={1}>
              Obteniendo información de WhatsApp
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : error ? (
        <MDBox p={3}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            <MDTypography variant="h6" fontWeight="bold">
              Error al cargar datos
            </MDTypography>
            <MDTypography variant="body2">{error}</MDTypography>
          </Alert>
          <MDBox display="flex" justifyContent="center" mt={2}>
            <MDButton variant="contained" color="primary" onClick={retryLoad} disabled={loading}>
              {loading ? "Reintentando..." : "Reintentar carga"}
            </MDButton>
          </MDBox>
        </MDBox>
      ) : showNoData ? (
        <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
          <HourglassEmpty sx={{ fontSize: 60, color: "warning.main", mb: 2 }} />
          <MDTypography variant="h5" fontWeight="bold" gutterBottom>
            No se pudieron cargar los datos
          </MDTypography>
          <MDTypography variant="body1" color="text.secondary" mb={3}>
            La carga de datos está tomando más tiempo de lo esperado.
          </MDTypography>
          <MDButton variant="contained" color="primary" onClick={retryLoad}>
            Reintentar carga
          </MDButton>
        </MDBox>
      ) : filteredConfiguraciones.length === 0 ? (
        <MDBox p={4} textAlign="center" bgcolor="background.paper" borderRadius="8px">
          <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
          <MDTypography variant="h5" fontWeight="bold" gutterBottom>
            No se encontraron configuraciones
          </MDTypography>
          <MDTypography variant="body1" color="text.secondary" mb={3}>
            No hay configuraciones que coincidan con los filtros aplicados.
          </MDTypography>
        </MDBox>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: "8px", overflow: "hidden" }}>
          <DataTable
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            showTotalEntries={true}
            noEndBorder={true}
            canSearch={false}
            pagination={{ variant: "gradient", color: "primary" }}
          />
          <MDBox
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ backgroundColor: "grey.50", borderTop: "1px solid", borderColor: "divider" }}
          >
            <MDBox display="flex" alignItems="center" gap={2}>
              <Chip
                label={`${filteredConfiguraciones.length} resultados`}
                size="small"
                color="primary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              />
            </MDBox>
            <MDTypography variant="caption" color="text.secondary">
              Mostrando {filteredConfiguraciones.length} de {totalConfiguraciones} configuraciones
            </MDTypography>
          </MDBox>
        </Paper>
      )}
    </MDBox>
  );
}

TableWhatsappCop.propTypes = {
  configuraciones: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onError: PropTypes.func,
  showNoData: PropTypes.bool,
  totalConfiguraciones: PropTypes.number,
  filtros: PropTypes.shape({
    grupo: PropTypes.string,
    activo: PropTypes.string,
    pulso: PropTypes.string,
    busqueda: PropTypes.string,
  }),
  onEditClick: PropTypes.func,
};

TableWhatsappCop.defaultProps = {
  configuraciones: [],
  loading: false,
  error: null,
  onError: null,
  showNoData: false,
  totalConfiguraciones: 0,
  filtros: { grupo: "todos", activo: "todos", pulso: "todos", busqueda: "" },
  onEditClick: null,
};

export default TableWhatsappCop;
